import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { IconNames } from '@ff-os/shared-react/components/icons/Icons';
import { RadioGroup } from '@ff-os/shared-react/components/radio-group/RadioGroup';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import classNames from 'classnames';
import { selectCompatibleBorderOptions } from '../../../../_redux-borders';
import { selectCompatiblePaperOptions } from '../../../../_redux-papers';
import { selectSizeOptions } from '../../../../_redux-sizes';
import { overwriteBorderSelectionIfIncompatible } from '../../../../helpers/overwriteBorderSelectionIfIncompatible';
import { overwritePaperSelectionIfIncompatible } from '../../../../helpers/overwritePaperSelectionIfIncompatible';
import { updateVariant } from '../../../../thunks/updateVariant';
import { Translatable, VariantOption } from '../../../../VariantOption';
import { VariantDropdown } from './VariantDropdown';
import styles from './VariantOptionGroup.module.scss';

const _VariantOptions = ({
    activeVariant,
    paperOptions, sizeOptions, borderOptions,
    updateVariant
}: Props) => {

    const { t } = useTranslation();
    const onSizeChange = useCallback((newSizeId: string) => {
        const paperId = overwritePaperSelectionIfIncompatible(activeVariant.paperId, newSizeId, paperOptions);
        const borderId = overwriteBorderSelectionIfIncompatible(
            activeVariant.borderId,
            newSizeId,
            paperId,
            borderOptions);
        updateVariant(paperId, newSizeId, borderId);
    }, [activeVariant, paperOptions, borderOptions, updateVariant]);

    const onPaperChange = (paperId: string) => {
        const borderId = overwriteBorderSelectionIfIncompatible(
            activeVariant.borderId,
            activeVariant.sizeId,
            paperId,
            borderOptions);
        updateVariant(paperId, activeVariant.sizeId, borderId);
    };

    const onBorderChange = (borderId: string) => {
        updateVariant(activeVariant.paperId, activeVariant.sizeId, borderId);
    };

    return (
        <Grid wrap gap='s'>

            <Cell className={styles.cell}>
                <VariantDropdown 
                    label={t('photo_prints.options.size.name')}
                    value={activeVariant.sizeId}
                    className={styles.dropdown}
                    onChange={onSizeChange}
                    options={sizeOptions} />
            </Cell>

            {paperOptions.length > 1 && (
                <Cell className={styles.cell}>
                    <RadioGroup label={t('photo_prints.options.paper.name')}>
                        {paperOptions.map(p => 
                            <RadioOption 
                                label={p.name} method={p.value} 
                                activeMethod={activeVariant.paperId} 
                                setMethod={onPaperChange} />
                        )}
                    </RadioGroup>
                </Cell>
            )}

            {borderOptions.length > 1 && (
                <Cell className={styles.cell}>
                    <RadioGroup label={t('photo_prints.options.border.name')}>
                        {borderOptions.map(p => 
                        <RadioOption 
                            label={p.name} method={p.value} 
                            activeMethod={activeVariant.borderId} 
                            icon={p.default ? "no-border" : "border"} setMethod={onBorderChange} />)}
                    </RadioGroup>
                </Cell>
            )}

        </Grid>
    );
};

type OwnProps = {
    groupId: string;
    activeVariant: PhotoPrintsJson.Variant;
};

const RadioOption = ({ method, activeMethod, setMethod, label, icon}: {
    method: string;
    activeMethod: string;
    setMethod: (method: string) => void;
    label: Translatable;
    icon?: IconNames;
}) => {
    const { t } = useTranslation();
    const text = t(label.key, label.args);
    const active = activeMethod === method;
    const classes = classNames(styles.option, {
        [styles.optionActive]: active
    });

    return (
        <div className={classes} onClick={() => setMethod(method)} title={text}>
            <p className={styles.label}>
                {icon ? <Icon className={styles.icon} type={icon} /> : null} {text}
            </p>
        </div>
    );
};

type Props = OwnProps & {
    paperOptions: VariantOption[];
    sizeOptions: VariantOption[];
    borderOptions: VariantOption[];
    updateVariant: (paperId: string | undefined, sizeId: string | undefined, borderId: string | undefined) => void;
}; 

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    paperOptions: selectCompatiblePaperOptions(state, ownProps.activeVariant.sizeId),
    sizeOptions: selectSizeOptions(state, ownProps.activeVariant.paperId),
    borderOptions: selectCompatibleBorderOptions(state, ownProps.activeVariant.sizeId, ownProps.activeVariant.paperId)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    updateVariant: (paperId: string | undefined, sizeId: string | undefined, borderId: string | undefined) => {
        dispatch(updateVariant(paperId, sizeId, ownProps.groupId, borderId));
    }
});

export const VariantOptionGroup = connect(
    mapStateToProps,
    mapDispatchToProps
)(_VariantOptions);